import { isNumber, last } from "lodash";
import {
    AddOn,
    BaseScheduleType,
    CatalogItemDisplaySection,
    CatalogItemVisibility,
    Experience,
    ExperienceStatus,
    Product,
    ProductCatalogItem,
    ScheduleDeparture,
    ScheduleType,
    UnitType,
} from "types";

export const isAllDay = (experience: Experience) => {
    if (!experience.schedules || experience.schedules?.length === 0) {
        return true;
    }

    const availableSchedules = experience.schedules.filter((schedule) => schedule.type === BaseScheduleType.AVAILABLE);
    if (availableSchedules.length === 0) {
        return true;
    }

    return last(availableSchedules)?.departure === ScheduleDeparture.VARIES;
};

export const isScheduled = (experience: Experience) => {
    return experience.scheduleType ? experience.scheduleType !== ScheduleType.OPEN : experience.isScheduled;
};

export const hasTimeScheduled = (experience: Experience) => {
    if (experience.schedules.length > 0) {
        return experience.schedules.some((schedule) => schedule.times && schedule.times.length > 0);
    }

    return experience.scheduleType ? experience.scheduleType === ScheduleType.TIME : false;
};

export const getExperienceMin = (experience: Experience): number => {
    return experience?.group?.orderMin ?? 1;
};

export const getDemographicCatalog = (product: Product) => {
    const demographics = product.catalog?.items?.filter((catalogItem) => {
        return catalogItem.unitType === UnitType.DEMOGRAPHIC;
    });
    return demographics ?? [];
};

export const getEquipmentCatalog = (experience: Experience) => {
    const equipment =
        experience.catalog?.items?.filter((catalogItem) => catalogItem.unitType === UnitType.EQUIPMENT) ?? [];
    return equipment;
};

export const getPrimaryUnitCatalog = (product: Product) => {
    const experience = product as Experience;
    return isEquipmentBased(experience) ? getEquipmentCatalog(experience) : getDemographicCatalog(product);
};

export const getDemographicById = (experience: Experience, id?: ID) => {
    return experience.catalog?.items.find((catalog) => catalog.template?.id === id);
};

export const getDemographicByUnitId = (experience: Experience, id: ID) => {
    return experience.catalog?.items.find((catalog) => catalog.template?.unit?.id === id);
};

export const getPublicAddons = (experience: Experience) => {
    return getAddOns(experience).filter((addon) => {
        return (addon as AddOn).visibility === CatalogItemVisibility.PUBLIC;
    });
};

export const getAddOns = (experience: Experience) => {
    return experience.catalog.items.filter(
        (catalogItem) =>
            [UnitType.ADDON, UnitType.PICKUP].includes(catalogItem.unitType) &&
            catalogItem.displaySections?.includes(CatalogItemDisplaySection.ADDONS),
    );
};

export const getSortedAddOns = (catalog: AddOn[]) => {
    return catalog.sort((a, b) => a.sequence - b.sequence);
};

export const hasMedia = (experience: Experience) => {
    return experience.medias.length > 0;
};

export const isDraft = (experience: Experience) => {
    return experience.status === ExperienceStatus.DRAFT;
};

export const isPricingConfigured = (demographicCatalog: ProductCatalogItem[]) => {
    if (demographicCatalog.length === 0) {
        return false;
    }

    return demographicCatalog.some((catalogItem) => {
        return isNumber(catalogItem.prices?.price ?? null) || isNumber(catalogItem.prices?.priceWithTaxes ?? null);
    });
};

export const isEquipmentBased = (experience: Experience): boolean => {
    return Boolean(experience.supportedCatalogItemTypes?.includes(UnitType.EQUIPMENT));
};

export const getDraftReasons = (experience: Experience) => {
    const draftReasons: string[] = [];

    if (isDraft(experience)) {
        const catalog = [...getDemographicCatalog(experience), ...getEquipmentCatalog(experience)];
        if (catalog.length === 0) {
            draftReasons.push("Demographics/Equipments are not configured");
        } else if (!isPricingConfigured(catalog)) {
            draftReasons.push("Pricing is not configured");
        }

        if (experience.virtualMeeting?.enabled && experience.schedules.length === 0) {
            draftReasons.push("Schedules are not configured");
        }
    }

    return draftReasons;
};

export const getAddonCatalog = (experience: Experience) => {
    const addOns = experience.catalog?.items?.filter((catalogItem) => {
        return (
            catalogItem.displaySections?.includes(CatalogItemDisplaySection.ADDONS) &&
            (catalogItem.unitType === UnitType.ADDON || catalogItem.unitType === UnitType.PICKUP)
        );
    });
    return addOns ?? [];
};
